// apiService.js
import { toast } from 'react-toastify';
import { parseAdminJsonResponse, wrapAdminPayload, isAdminPayloadProtectionEnabled } from './adminPayloadCrypto';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/zuntly/admin`;
// const API_BASE_URL = "http://localhost:3600/zuntly/admin";

export const getAdminApiHeaders = (extra = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem("token"),
        ...extra,
    };

    try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (userData && userData.role === 1 && userData.selectedBranch) {
            headers['selectedbranch'] = userData.selectedBranch;
        }
    } catch (error) {
        console.warn("Error parsing userData from localStorage:", error);
    }

    return headers;
};

const getHeaders = () => getAdminApiHeaders();

const handleResponse = async (response) => {
    try {
        const data = await parseAdminJsonResponse(response);
        if (!response.ok) {
            // Use detailed error message if available, otherwise use message
            const detailedError = data.error || data.message || `HTTP error! status: ${response.status}`;
            console.warn("API Error:", detailedError);

            // Handle token expiration error
            if (data.error === "token Expired") {
                localStorage.removeItem("userData");
                localStorage.removeItem("token");
                localStorage.removeItem("sessionId");
                window.location.href = "/login"; // Redirect to login page
            }
            
            // Handle session invalidation (logged out from another device)
            if (data.error === "session_invalidated" || data.forceLogout === true) {
                console.warn("Session invalidated - user logged in on another device");
                
                // Show toast notification
                toast.warning("You have been logged out because you logged in on another device. Only 2 simultaneous sessions are allowed.", {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: false,
                    draggable: false,
                });
                
                // Clear storage and redirect after 5 seconds
                setTimeout(() => {
                    localStorage.removeItem("userData");
                    localStorage.removeItem("token");
                    localStorage.removeItem("sessionId");
                    window.location.href = "/login";
                }, 5000);
                
                // Return to prevent further processing
                return { success: false, sessionInvalidated: true };
            }

            // Handle branch inactive (branch deactivated by admin)
            if (data.error === "branch_inactive") {
                console.warn("Branch is inactive - deactivated by admin");
                
                toast.error("Branch is not active. Please contact Admin.", {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: false,
                    draggable: false,
                });
                
                setTimeout(() => {
                    localStorage.removeItem("userData");
                    localStorage.removeItem("token");
                    localStorage.removeItem("sessionId");
                    window.location.href = "/login";
                }, 3000);
                
                return { success: false, branchInactive: true };
            }

            // Create error with detailed message
            const error = new Error(detailedError);
            error.statusCode = response.status;
            error.errorData = data;
            throw error; // Re-throw to be caught by the caller
        }
        return data;
    } catch (error) {
        console.warn("JSON Parsing Error:", error);
        throw error; // Re-throw the parsing error
    }
};

/**
 * Shared admin fetch for call sites that bypass apiRequest.
 * Encrypts JSON bodies and decrypts protected responses transparently.
 */
export const adminFetchJson = async (endpointPathOrUrl, { method = 'POST', body = null, headers: extraHeaders = {} } = {}) => {
    const url = String(endpointPathOrUrl).startsWith('http')
        ? endpointPathOrUrl
        : `${API_BASE_URL}${endpointPathOrUrl}`;
    const headers = getAdminApiHeaders(extraHeaders);
    const options = { method, headers };
    if (body !== null && body !== undefined) {
        const payload = typeof body === 'string' ? JSON.parse(body) : body;
        if (isAdminPayloadProtectionEnabled()) {
            options.body = JSON.stringify(await wrapAdminPayload(payload));
            headers['X-Zuntly-Payload'] = '1';
        } else {
            options.body = JSON.stringify(payload);
        }
    }
    const response = await fetch(url, options);
    const data = await parseAdminJsonResponse(response);
    return { response, data };
};

const apiRequest = async (endpointPath, method = 'POST', body = null) => {
    const url = `${API_BASE_URL}${endpointPath}`; // Construct full URL
    const options = {
        method,
        headers: getHeaders(),
    };

    if (body !== null && body !== undefined) {
        if (isAdminPayloadProtectionEnabled()) {
            options.body = JSON.stringify(await wrapAdminPayload(body));
            options.headers['X-Zuntly-Payload'] = '1';
        } else {
            options.body = JSON.stringify(body);
        }
    }

    try {
        const response = await fetch(url, options);
        return handleResponse(response);
    } catch (error) {
        console.warn("Fetch Error:", error); // Log network errors
        throw error; // Re-throw for caller to handle
    }
};

export default apiRequest;
